/**
 * CreateBuddyGroupModal — Dialog to create a new buddy group.
 *
 * Fields: group name (optional).
 * On success: navigates to the new group's detail page.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Loader2, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { buddyGroupOpsKeys } from '@/api/extensions/useBuddyGroupOps';
import { createBuddyGroup } from '@/services/buddy-group.service';
import { getDefaultBuddyGroupName } from './getDefaultBuddyGroupName';

interface CreateBuddyGroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingNames?: string[];
}

export function CreateBuddyGroupModal({
  open,
  onOpenChange,
  existingNames = [],
}: CreateBuddyGroupModalProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [groupName, setGroupName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setGroupName(getDefaultBuddyGroupName(existingNames));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async () => {
    const trimmed = groupName.trim();
    if (!trimmed) {
      setError('Give your buddy group a name.');
      return;
    }
    if (existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase())) {
      setError('A group with this name already exists. Please choose a different name.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await createBuddyGroup({
        group_name: trimmed,
      });
      // Invalidate all buddy group queries
      qc.invalidateQueries({ queryKey: buddyGroupOpsKeys.all });
      onOpenChange(false);
      // Navigate to the new group's detail page
      navigate(`/my/buddy-groups/${result.buddy_group.id}`);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create group');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setGroupName('');
      setError(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create Buddy Group
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="bg-group-name">
              Group Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="bg-group-name"
              placeholder="e.g. The Ropers"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !submitting) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              maxLength={100}
              required
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Create Group
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
